<template>
  <section
    :class="[`SpecificationBlock SpecificationBlock--${variant}`]"
    :aria-label="$t('accessibility.description.specificationBlock')">
    <article class="AiSpecification Layout">
      <div class="Layout-section">
        <grid-item sm="4" md="2" offset-md="1">
          <ai-typo
            :class="[`AiSpecification-title AiSpecification-title--${variant}`]"
            as="h2"
            variant="heading-02">
            {{ $t(`hotel.specification.title`) }}
          </ai-typo>
        </grid-item>
        <grid-item sm="4" md="7" offset-md="1">
          <ul
            id="AiSpecification-list"
            class="AiSpecification-item-wrapper"
            :aria-label="$t(`hotel.specification.title`)">
            <ai-pictograms
              v-for="aiSpecification of specificationLoaded"
              :key="`AiSpecification-item-${aiSpecification.id}`"
              class="AiSpecification-item"
              as="li"
              :size="40"
              variant="inverse"
              icon-position="center"
              facilities
              :title="aiSpecification.title"
              :icon-name="aiSpecification.icon"
              icon-namespace="amenities"
              color="gold-300"
              typo-color="white"
              typo-variant="icon-text"
              spaced />
          </ul>
          <ai-button
            v-if="attach"
            :class="[
              'AiSpecification-button',
              { 'AiSpecification-button--expanded': expanded },
            ]"
            :aria-expanded="expanded"
            :icon-right="expanded ? 'chevron' : 'chevron-down'"
            slim
            :label="buttonLabel"
            variant="tertiary"
            aria-controls="AiSpecification-list"
            @click="toggleExpanded" />
        </grid-item>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import AiTypo from '../../atoms/AiTypo/AiTypo.vue';
import AiPictograms from '../../atoms/AiPictograms/AiPictograms.vue';
import AiButton from '../../atoms/AiButton/AiButton.vue';

import type { AiSpecification } from './types';

export interface Props {
  specification: AiSpecification[];
  variant: 'positive' | 'inverse';
}

const props = withDefaults(defineProps<Props>(), {
  specification: () => [],
  variant: 'positive',
});

const { t } = useI18n();

const itemLength = ref<number>(9);
const expanded = ref<boolean>(false);

const specificationLoaded = computed(() =>
  props.specification.slice(0, itemLength.value),
);

const buttonLabel = computed<string>(() =>
  expanded.value
    ? t('hotel.specification.buttonLabelLess')
    : t('hotel.specification.buttonLabelAll', {
        specification: props.specification.length,
      }),
);

const attach = computed<boolean>(() => props.specification.length > 9);

const toggleExpanded = () => {
  if (!expanded.value) {
    itemLength.value = props.specification.length;
  } else {
    itemLength.value = 9;
  }
  expanded.value = !expanded.value;
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mixins';
@use '@/assets/styles/utilities/mq';

.SpecificationBlock {
  padding: constants.$padding-07 0;
  background-color: colors.$gold-100;

  &--inverse {
    background: linear-gradient(
      105.41deg,
      colors.$stratos-900 10.8%,
      colors.$stratos-700 165.89%
    );
  }
}

.AiSpecification-title {
  margin-bottom: constants.$margin-04;
  @media (mq.$from-medium) {
    margin-bottom: 0;
  }
  &--inverse {
    color: colors.$white;
  }
}

.AiSpecification-item-wrapper {
  margin-bottom: constants.$margin-04;
  @media (mq.$from-medium) {
    margin-bottom: 0;
    display: flex;
    flex-wrap: wrap;
  }
}

.AiSpecification-item {
  text-transform: uppercase;
  margin-bottom: constants.$inner-03;
  &:last-child {
    margin-bottom: constants.$margin-03;
  }
  @media (mq.$from-medium) {
    margin: 0 0 constants.$margin-03 0;
    flex-direction: row;
    flex-basis: calc(100% / 3);
  }
}

.AiSpecification-button {
  color: colors.$gold-300;
}
</style>
